import { useAlarms } from "@/hooks/useAlarms";
import type { Alarm } from "@/data/types";

/**
 * Severity rank used to pick the worst active alarm. Higher = worse.
 * Moved verbatim from HealthBanner so the banner and the shell-level
 * IncidentStrip can never disagree on "which alarm is on top".
 */
export function severityOrder(s: string): number {
  return ({ critical: 4, major: 3, minor: 2, warning: 1 } as Record<string, number>)[s] ?? 0;
}

/**
 * Single source of truth for worst-alarm selection (F24). Both HealthBanner
 * (Overview) and IncidentStrip (every other route) consume this, so the two
 * critical surfaces always cite the same incident.
 *
 * Note: `top` sorts a COPIED array — HealthBanner historically sorted the
 * react-query data array in place, mutating the shared cache (a latent bug);
 * the copy here fixes it for both consumers.
 */
export function useWorstAlarm(): {
  isError: boolean;
  active: Alarm[];
  criticalCount: number;
  criticalActive: boolean;
  top: Alarm | undefined;
} {
  const { data: alarms = [], isError } = useAlarms();
  const active = alarms.filter((a) => a.status === "active");
  const top = [...active].sort((a, b) => severityOrder(b.severity) - severityOrder(a.severity))[0];
  const criticalCount = active.filter((a) => a.severity === "critical").length;
  return { isError, active, criticalCount, criticalActive: criticalCount > 0, top };
}
